import { ComponentClass, createElement, FunctionComponent } from 'react';

import { Button, ButtonProps, DialogProps, IconButton, IconButtonProps } from '@mui/material';

import { useDialogStatus } from 'hooks/common';

type DialogButtonProps<T> = {
  label?: string;
  dialog: FunctionComponent<T> | ComponentClass<T, any>;
  dialogProps?: Omit<T, 'open' | 'onClose'>;
  className?: string;
  paddingStyle?: 'big' | 'small';
};

/**
 * Renders a button with internal dialog visible state,
 * should receive a valid dialog and optional dialogs props
 *
 * *Note* -> Mounts and unmounts the provided dialog prop on visible state changes,
 * fits our use case for forms
 */
export function DialogButton<T extends DialogProps>({
  label,
  dialog,
  dialogProps,
  startIcon,
  paddingStyle = 'small',
  ...btnProps
}: DialogButtonProps<T> & Omit<ButtonProps & IconButtonProps, 'onClick'>) {
  const { isOpen, onShow, onHide } = useDialogStatus();
  return (
    <>
      {label ? (
        <Button
          color={btnProps.color}
          variant={btnProps.variant || 'contained'}
          onClick={onShow}
          sx={{
            ...(paddingStyle === 'big' && {
              padding: theme => theme.spacing(1.25),
            }),
            ...(paddingStyle === 'small' && {
              padding: theme => theme.spacing(0.25),
            }),
          }}
          startIcon={startIcon}
          {...btnProps}
        >
          {label}
        </Button>
      ) : (
        <IconButton
          sx={{
            ...(paddingStyle === 'big' && {
              padding: theme => theme.spacing(1.25),
            }),
            ...(paddingStyle === 'small' && {
              padding: theme => theme.spacing(0.25),
            }),
          }}
          onClick={onShow}
          color={btnProps.color}
          {...btnProps}
          size="large"
        >
          {startIcon}
        </IconButton>
      )}
      {isOpen && createElement(dialog, { open: isOpen, onClose: onHide, ...dialogProps } as any)}
    </>
  );
}
